import { useQuery } from '@tanstack/react-query';

import type { Quote, QuoteListFilters } from '../types';
import { quotesApi, quotesViewApi } from './client';
import { quoteKeys } from './keys';

/**
 * useQuoteList — paginated list of quotes for the table view.
 *
 * Default page size mirrors the DataTable default (10). The legacy backend
 * doesn't expose a server-side status filter for `/Quote` so all filtering
 * happens client-side; the hook therefore always requests a wide page (200 by
 * default) so the table + Kanban share a single cache entry.
 */
export function useQuoteList(
  filters: QuoteListFilters = {},
  options: { enabled?: boolean } = {},
) {
  const merged: QuoteListFilters = {
    pageNumber: filters.pageNumber ?? 1,
    pageSize: filters.pageSize ?? 200,
  };
  return useQuery({
    queryKey: quoteKeys.list(merged),
    queryFn: () => quotesApi.list(merged),
    // Allows callers (e.g. the lead → quotes popup) to defer the single
    // `GET /Quote` fetch until it's actually needed. The default-filters query
    // key is shared with the Quotes list page, so a warm cache serves instantly.
    enabled: options.enabled ?? true,
  });
}

/**
 * useQuotesForKanban — Kanban view fetch.
 *
 * Distinct query key from `useQuoteList` so the Kanban can opt into a wider
 * page (default 500) without invalidating the table cache. Switching between
 * views does not refetch when both caches are warm.
 */
export function useQuotesForKanban(filters: QuoteListFilters = {}) {
  const merged: QuoteListFilters = {
    pageNumber: filters.pageNumber ?? 1,
    pageSize: filters.pageSize ?? 200,
  };
  return useQuery({
    queryKey: quoteKeys.kanban(merged),
    queryFn: () => quotesApi.list(merged),
  });
}

/**
 * useQuote — single quote by id, resolved from the cached list when possible
 * and from the dedicated `/Quote/:id` endpoint otherwise.
 *
 * Agent B/C will likely want a heavier variant with `includeItems = true`. For
 * the list/Kanban surface (Agent A) the cached row is sufficient.
 */
export function useQuote(id: string | undefined) {
  return useQuery<Quote>({
    queryKey: id ? quoteKeys.detail(id) : quoteKeys.details(),
    queryFn: () => quotesApi.getById(id as string, false),
    enabled: !!id,
  });
}

/**
 * useQuoteStatusHistory — status-transition history for the lifecycle timeline
 * on the Quote view page. Reads the existing `/Quote/:id/StatusHistory`
 * endpoint (no backend change). Returns `[]` on empty/missing history.
 */
export function useQuoteStatusHistory(id: string | undefined) {
  return useQuery({
    queryKey: id ? quoteKeys.statusHistory(id) : quoteKeys.statuses(),
    queryFn: () => quotesViewApi.getStatusHistory(id as string),
    enabled: !!id,
  });
}
